import { useState, useEffect, useMemo, useRef } from 'react';

const C = {
  bg:'#0a0a0a', card:'#161616', cardHi:'#1f1f1f', border:'#2a2a2a',
  text:'#fafafa', muted:'#a1a1aa', dim:'#71717a',
  alex:'#ef4444', group:'#f5c518', win:'#22c55e', multi:'#8b5cf6',
};

export function ChatModal({ open, onClose, chats, presence, me, send }) {
  const [target, setTarget] = useState('all'); // 'all' | userId
  const [text, setText] = useState('');
  const listRef = useRef(null);

  const myUserId = me?.userId;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!open) return;
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [chats?.length, open, target]);

  // Unread counter — must be a hook called every render
  const unread = useMemo(() => {
    const m = new Map();
    (chats || []).forEach(msg => {
      if (msg.from === myUserId) return;
      if (msg.to !== myUserId && msg.to !== 'all') return;
      const key = msg.to === 'all' ? 'all' : msg.from;
      m.set(key, (m.get(key) || 0) + 1);
    });
    return m;
  }, [chats, myUserId]);

  if (!open) return null;

  const visible = (chats || []).filter(m => {
    if (target === 'all') return m.to === 'all';
    return (m.from === myUserId && m.to === target) || (m.from === target && m.to === myUserId);
  });

  const onSend = () => {
    const t = text.trim();
    if (!t) return;
    send({ type: 'chat-msg', to: target, text: t });
    setText('');
  };

  const others = presence.filter(p => p.userId !== myUserId);
  const targetName = target === 'all' ? 'Alle' : (presence.find(p => p.userId === target)?.name || '?');

  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center" style={{ background: 'rgba(0,0,0,0.8)' }} onClick={onClose}>
      <div className="w-full sm:max-w-md h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden"
        style={{ background: C.card, border: `1px solid ${C.border}` }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ background: C.card, borderColor: C.border }}>
          <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.muted }}>💬 Chat · {targetName}</div>
          <button onClick={onClose} className="text-lg leading-none w-8 h-8 flex items-center justify-center" style={{ color: C.dim }}>×</button>
        </div>

        {/* Recipient tabs */}
        <div className="overflow-x-auto px-2 py-2 border-b flex gap-1.5 flex-shrink-0" style={{ borderColor: C.border, background: C.card }}>
          <ChatTab active={target === 'all'} label="Alle" badge={unread.get('all')} onClick={() => setTarget('all')} color={C.multi} />
          {others.map(p => (
            <ChatTab key={p.userId} active={target === p.userId}
              label={p.name + (p.role === 'alex' ? ' ★' : p.role === 'leader' ? ' 🎙' : '')}
              badge={unread.get(p.userId)}
              dim={!p.active}
              onClick={() => setTarget(p.userId)}
              color={p.role === 'alex' ? C.alex : p.role === 'leader' ? C.alex : C.group} />
          ))}
        </div>

        {/* Messages */}
        <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2" style={{ background: C.bg }}>
          {visible.length === 0 && (
            <div className="text-center text-xs mt-8" style={{ color: C.dim }}>
              {target === 'all' ? 'Keine Nachrichten an alle. Schreib was rein!' : 'Noch keine Nachrichten. Sag Hallo!'}
            </div>
          )}
          {visible.map(m => {
            const mine = m.from === myUserId;
            const accent = m.fromRole === 'alex' ? C.alex : m.fromRole === 'leader' ? C.alex : C.group;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[80%] rounded-2xl px-3 py-2"
                  style={{ background: mine ? C.win : C.cardHi, color: mine ? '#000' : C.text }}>
                  {!mine && <div className="text-[10px] uppercase tracking-widest font-bold mb-0.5" style={{ color: accent }}>{m.fromName}</div>}
                  <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                  <div className="text-[9px] mt-1 opacity-60">{new Date(m.ts).toLocaleTimeString().slice(0, 5)}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Input */}
        <div className="flex items-center gap-2 px-3 py-3 border-t flex-shrink-0" style={{ borderColor: C.border, background: C.card }}>
          <textarea value={text} onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
            placeholder={target === 'all' ? 'An alle…' : `An ${targetName}…`}
            rows={1} maxLength={500}
            className="flex-1 rounded-lg px-3 py-2 text-sm resize-none outline-none"
            style={{ background: C.cardHi, color: C.text, border: `1px solid ${C.border}`, maxHeight: 80 }} />
          <button onClick={onSend} disabled={!text.trim()}
            className="rounded-lg px-4 py-2 text-sm font-bold disabled:opacity-30"
            style={{ background: C.multi, color: '#fff' }}>Senden</button>
        </div>
      </div>
    </div>
  );
}

function ChatTab({ active, label, badge, onClick, color, dim }) {
  return (
    <button onClick={onClick}
      className="rounded-md px-3 py-1.5 text-xs font-bold whitespace-nowrap active:scale-95"
      style={{
        background: active ? C.cardHi : C.card,
        color: dim ? C.dim : (active ? color : C.muted),
        border: `1px solid ${active ? color : C.border}`,
      }}>
      {label}
      {badge ? <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px]"
                    style={{ background: C.win, color: '#000' }}>{badge}</span> : null}
    </button>
  );
}
